import { useState, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { InboundHeaderCard } from "ui-components/Layout";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { useQuery } from "@tanstack/react-query";
import { AddButton, DeleteButton } from "ui-components/IconButton";
import { ReadonlyText } from "ui-components/Form";
import { getBillingChargeable, getNewConfiguration } from "services/data.billing.service";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import BasicTable from "ui-components/tables/BasicTable"; // adjust path to match your project

// >>> NEW: hides the search icon inside each column's filter box.
// Targets common icon markup (MUI Icon/SvgIcon/InputAdornment) so it works
// without needing FilterInput's exact internals — if it doesn't catch the
// icon, share FilterInput's source and I'll target it precisely instead.
const StyledBasicTable = styled(BasicTable)({
  "th svg, th .material-icons, th .MuiInputAdornment-root": {
    display: "none",
  },
});

function EditBillingChargeable({ activeTab, invoiceNumber, localData, setLocalData, setChargeableData, handleSubmit, setAlert, customerCode, viewPricePermission }) {

  const updateRow = (id, field, value) => {
    setLocalData((prev) =>
      prev.map((x) => {
        if (x.id !== id) return x;
        const updated = { ...x, [field]: value };
        if (field === "chargeQty" && updated.chargeQty < 0) updated.chargeQty = 0;
        if (Boolean(updated.chargeQty) && Boolean(updated.chargeRate)) {
          updated.totalAmount = updated.chargeQty * updated.chargeRate;
        }
        return updated;
      })
    );
  };

  const handleDeleteRow = (id) => {
    setLocalData((prev) => prev.filter((item) => item.id !== id));
  };

  const handleAddRow = (row) => {
    const nextId = localData.length > 0 ? Math.max(...localData.map((item) => item.id)) + 1 : 1;
    const newRow = {
      documentDate: null,
      id: nextId,
      customerCode: row.customerCode,
      category: null,
      billingParameter: null,
      billItemCode: null,
      criteria: null,
      chargeType: "Flat Rate",
      chargeDesc: "Flat Rate",
      actualQty: 1,
      active: "Active",
      billingName: null,
      billingNo: 0,
      createdBy: null,
      createdOn: null,
      lowerLimit: 0,
      rate: 0,
      status: "O",
      unit: null,
      uom: null,
      updatedBy: null,
      updatedOn: null,
      upperLimit: 0,
      totalAmount: 0,
      supportingCode: row.supportingCode,
      invoiceNumber: row.invoiceNumber,
    };
    setLocalData((prev) => [...prev, newRow]);
  };

  const chargeablesColumns = useMemo(() => [
    {
      accessorKey: "billItemCode",
      header: "Bill Item Code",
      cell: ({ row }) =>
        activeTab === "VAS" ? (
          <MDInput
            value={row.original.billItemCode ?? ""}
            onChange={(e) => updateRow(row.original.id, "billItemCode", e.target.value)}
          />
        ) : (
          row.original.billItemCode
        ),
    },
    {
      accessorKey: "ref2",
      header: "Customer Reference Number",
      // >>> CHANGED: plain text, no longer editable
    },
    {
      accessorKey: "documentCode",
      header: "Document Number",
      // >>> CHANGED: plain text, no longer editable
    },
    {
      accessorKey: "documentDate",
      header: "Document Date",
      // >>> CHANGED: plain formatted text, no longer a DatePicker
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? dayjs(value).format("DD / MM / YYYY") : "";
      },
    },
    {
      accessorKey: "ref1",
      header: "Ref 1",
      // >>> CHANGED: plain text, no longer editable
    },
    {
      accessorKey: "actualQty",
      header: "Actual Quantity (Piece)",
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? "" : value.toLocaleString("en-US");
      },
    },
    {
      accessorKey: "chargeDesc",
      header: "Charge Description",
    },
    {
      accessorKey: "chargeType",
      header: "Charge Type",
    },
    // {
    //   accessorKey: "chargeQty",
    //   header: "Chargeable Quantity",
    //   // >>> CHANGED: shows chargeQty, but falls back to chargeableAmount when chargeQty is 0
    //   cell: ({ row }) => {
    //     const value = row.original.chargeQty ? row.original.chargeQty : row.original.chargeableAmount;
    //     return value === null || value === undefined
    //       ? ""
    //       : value.toLocaleString("en-US", { minimumFractionDigits: 5, maximumFractionDigits: 5 });
    //   },
    // },
    {
  accessorKey: "chargeQty",
  header: "Chargeable Quantity",
  cell: ({ row }) => {
    const value = row.original.chargeQty ? row.original.chargeQty : row.original.chargeableAmount;

    if (activeTab === "Storage") {
      return (
        <MDInput
          type="number"
          value={row.original.chargeQty ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = raw === "" ? 0 : parseFloat(raw);
            updateRow(row.original.id, "chargeQty", Number.isNaN(parsed) ? 0 : parsed);
          }}
        />
      );
    }

    return value === null || value === undefined
      ? ""
      : value.toLocaleString("en-US", { minimumFractionDigits: 5, maximumFractionDigits: 5 });
  },
},
    {
      accessorKey: "chargeRate",
      header: "Charge Rate",
      // >>> CHANGED: plain formatted text, no longer editable
      cell: ({ getValue }) => {
        if (!viewPricePermission) return "**";
        const value = getValue();
        return value === null || value === undefined
          ? ""
          : value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      accessorKey: "totalAmount",
      header: "Amount",
      cell: ({ getValue }) => {
        if (!viewPricePermission) return "**";
        const value = getValue();
        return value === null || value === undefined
          ? ""
          : value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      footer: ({ table }) => {
        if (!viewPricePermission) return "Total Amount: **";
        const total = table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + (row.original.totalAmount || 0), 0);
        return `Total Amount: ${total.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <>
          {activeTab === "VAS" && (
            <AddButton title="Add" onClick={() => handleAddRow(row.original)} />
          )}
          <DeleteButton title="Delete" onClick={() => handleDeleteRow(row.original.id)} />
        </>
      ),
    },
  ], [activeTab, viewPricePermission]);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const { isFetching } = useQuery({
    queryKey: ["billing-chargeable", { invoiceNumber, activeTab }],
    queryFn: () => getBillingChargeable({ invoiceNumber, activeTab }),
    onSuccess: (data) => {
      const result = data.data.data.map((x) => {
        const { documentDate, ...remaining } = x;
        var newDate = documentDate ? dayjs(documentDate, "DD / MM / YYYY").toDate() : null;
        return { documentDate: newDate, ...remaining };
      });
      setLocalData(structuredClone(result));
      setChargeableData(structuredClone(result));
    },
    onError,
  });

  const { mutate: fetchNewConfig, isLoading: isFetchingNewConfig } = useMutation({
    mutationFn: (req) => getNewConfiguration(req),
    onSuccess: (data) => {
      const newConfig = data?.data?.data || [];
      setLocalData((prev) => {
        const existingCodes = new Set(prev.map((item) => item.billItemCode?.trim()));
        const filtered = newConfig.filter((item) => !existingCodes.has(item.criteria?.trim()));
        const mapped = filtered.map((item) => ({
          billItemCode: item.criteria?.trim(),
          chargeType: item.chargeType,
          chargeDesc: item.chargeType,
          chargeRate: item.rate ?? 0,
          customerCode: item.customerCode,
          billCategory: prev[0]?.billCategory,
          billingNo: item.billingNo,
          billingName: item.billingName,
          createdBy: item.createdBy,
          createdOn: item.createdOn,
          status: item.status,
          id: item.id,
          actualQty: 1,
          chargeQty: 0,
          chargeableAmount: 0,
          totalAmount: 0,
          documentCode: prev[0]?.documentCode ?? null,
          documentDate: prev[0]?.documentDate ?? null,
          supportingCode: prev[0]?.supportingCode ?? null,
          billLineNo: prev[0]?.billLineNo ?? null,
        }));
        return [...prev, ...mapped];
      });
    },
    onError: (err) => {
      console.error("Failed to fetch new configuration", err);
    },
  });

  const onAdd = () => {
    fetchNewConfig({ activeTab, customerCode });
  };

  return (
    <>
      <InboundHeaderCard lg={12}>
        {isFetching && <LoadingDialog title="Fetching Billing Chargeables" />}

        <div
          style={{
            display: "flex",
            justifyContent: "start",
            alignItems: "start",
            height: "100%",
          }}
        >
          <MDButton color="info" variant="gradient" onClick={handleSubmit}>
            Save
          </MDButton>
        </div>
        <form noValidate>
          <br></br>

          <div>
            <Grid item xs={1.5}>
              {" "}
              <ReadonlyText
                field={{ name: "invoiceNumber", value: invoiceNumber, label: "Invoice Number" }}
              />
            </Grid>
            <br />
          </div>

          <div>
            <br></br>
            {/* >>> CHANGED: back to BasicTable; StyledBasicTable hides the filter-box search icon */}
            <StyledBasicTable columns={chargeablesColumns} data={localData} />
          </div>
        </form>
      </InboundHeaderCard>
    </>
  );
}

export default EditBillingChargeable;

export function validateData(data) {
  let message = [];
  if (data.some((x) => !Boolean(x.documentCode))) message.push("Invalid Document Code");
  return message;
}
